import { ref } from 'vue'
import { defineStore } from 'pinia'
import Log from '@/addons/logger'

import type {
  notificationGroup,
  notificationGroupData,
} from '@/abs-core/shared/types'
import LoaderController from '@/components/loader/LoaderController'
import { PageContexts } from '@/addons/enums'

/**
 * TODO search and rework addNotificationGroup
 */
export const useNotificationsStore = defineStore('NotificationsStore', () => {
  /**
   * Notification tabs registry:
   *
   * It contains all the notification tabs that should be visible
   * on the left side of the application, such as:
   *
   *  * promemoria
   *  * Report Click And Collect (CEC)
   *  * Prmotions
   *
   * @private
   */
  const _registry = ref<notificationGroup[]>([])

  /**
   *
   */
  const notifications = ref<notificationGroupData[]>([])

  /**
   * Adds a new notification group to the registry or updates an existing one.
   *
   * This function takes a weight value and a function to retrieve the notification group data.
   * It checks if a group with the same anchor already exists in the registry. If so, it updates the
   * existing group with the new weight and retrieve function. Otherwise, it adds a new group to
   * the registry.
   *
   * @param {notificationGroup} data - the notification group
   * @returns {void}
   */
  const addNotificationGroup = (data: notificationGroup): void => {
    const { anchor } = data

    const existingIndex = _registry.value.findIndex(
      (group) => group.anchor === anchor,
    )

    if (existingIndex === -1) {
      _registry.value.push(data)
    }
    Log.info(`added notification group: ${anchor}`)
  }

  /**
   * Returns the list of notification group data sorted by weight.
   *
   * This function iterates through the _registry array, which contains all the registered notification groups.
   * It sorts the groups by their weight property, then calls the get() function on each group to retrieve its data.
   * The resulting array of notificationGroupData objects is returned.
   *
   * @returns {notificationGroup[]} - An array of notification group data, sorted by weight.
   */
  const getNotificationGroups = (): notificationGroup[] => {
    const data: notificationGroup[] = []
    Object.keys(_registry.value)
      .filter((g) => _registry.value[g].enabled())
      .sort((a, b) => _registry.value[a].weight - _registry.value[b].weight)
      .forEach((k) => {
        data.push(_registry.value[k])
      })
    return data
  }

  /**
   * Retrieves notification groups, processes each group's notifications,
   * and commits the non-empty groups to the store.
   */
  const fetchNotifications = async () => {
    LoaderController.show({ section: PageContexts.GENERIC })
    const groups = getNotificationGroups()

    // Process each group to gather notifications.ts
    const data: notificationGroupData[] = await Promise.all(
      groups.map(async (group) => {
        const notifications = await group.retrieveNotifications()
        return {
          label: group.label,
          anchor: group.anchor,
          counter: notifications.length,
          notifications,
        }
      }),
    )
    LoaderController.hide()
    // Filter out empty notification groups and commit to state
    notifications.value = data.filter((group) => group.notifications.length > 0)
  }

  return {
    notifications,
    addNotificationGroup,
    getNotificationGroups,
    fetchNotifications,
  }
})
